// Client-side shapes for the SAFed lifecycle API at /api/safed-flow (Session BDB). Types only; the calls are in api.ts.
export type SafedLifecycleStateView = "not_assessed" | "recommended" | "due" | "authorised" | "booked" | "done" | "failed";

export const SAFED_STATE_LABEL_VIEW: Record<SafedLifecycleStateView, string> = {
  not_assessed: "Not assessed",
  recommended: "Recommended",
  due: "Due",
  authorised: "Authorised",
  booked: "Booked",
  done: "Done",
  failed: "Failed",
};

export interface SafedLifecycleTestView {
  testCode: string;
  name: string;
  cls: "STANDARD" | "STRESS";
  state: SafedLifecycleStateView;
  stateLabel: string;
  lastTestDate: string | null;
  nextDueDate: string | null;
  openJobId: string | null;
}

export interface SafedLiftLifecycleView {
  assetId: string;
  liftRef: string | null;
  siteId: string | null;
  siteName: string | null;
  customerId: string | null;
  customerName: string | null;
  driveType: "traction" | "hydraulic" | "both";
  tests: SafedLifecycleTestView[];
  counts: Record<SafedLifecycleStateView, number>;
}

export type SafedRecommendationSourceView = "examiner_report" | "insurer" | "our_inspection" | "customer_request" | "no_evidence";

export const SAFED_RECOMMENDATION_SOURCES_VIEW: { value: SafedRecommendationSourceView; label: string }[] = [
  { value: "examiner_report", label: "Examiner's report" },
  { value: "insurer", label: "Insurer's report" },
  { value: "our_inspection", label: "Our own inspection" },
  { value: "customer_request", label: "Customer's request" },
  { value: "no_evidence", label: "Nothing on file for the last five years" },
];

export interface SafedRecommendationView {
  id: string;
  assetId: string;
  source: SafedRecommendationSourceView;
  sourceLabel: string;
  documentRef: string | null;
  documentDate: string | null;
  recommendedText: string;
  tests: { id: string; name: string; cls: string }[];
  dismissedAt: string | null;
  dismissedReason: string | null;
  open: boolean;
  createdAt: string;
}

export type SafedQuoteStatusView = "draft" | "drafted" | "accepted" | "declined";

export interface SafedQuoteView {
  id: string;
  reference: string;
  assetId: string;
  recommendationId: string | null;
  tests: { id: string; name: string; cls: string }[];
  dayType: "full_day" | "half_day";
  engineers: number;
  amountPence: number;
  extrasOnQuotation: string[];
  recipientName: string | null;
  recipientEmail: string | null;
  status: SafedQuoteStatusView;
  draftedAt: string | null;
  acceptedAt: string | null;
  acceptedByName: string | null;
  acceptedVia: string | null;
  acceptanceNote: string | null;
  declinedAt: string | null;
  agreedExtras: { description: string; amountPence: number }[];
  jobIds: string[];
  createdAt: string;
}

export interface SafedQuotePlanView {
  tests: { id: string; name: string; cls: string }[];
  stress: boolean;
  engineers: 1 | 2;
  amountPence: number;
  extras: string[];
  extraLabels: string[];
  dayLabel: string;
  recipientEmail: string | null;
  recipientName: string | null;
  notes: string[];
}

export interface SafedAttentionItemView extends Omit<SafedLiftLifecycleView, "tests"> {
  tests: SafedLifecycleTestView[];
}

// The SAFed tests page (Session BDC): the lifecycle plus the two stages a quotation adds (quoted, accepted). Never "not assessed".
export type SafedWorklistStateView = "recommended" | "due" | "quoted" | "accepted" | "authorised" | "booked" | "done" | "failed";

export const SAFED_WORKLIST_ORDER_VIEW: SafedWorklistStateView[] = ["recommended", "due", "quoted", "accepted", "authorised", "booked", "done", "failed"];

export const SAFED_WORKLIST_LABEL_VIEW: Record<SafedWorklistStateView, string> = {
  recommended: "Recommended",
  due: "Due",
  quoted: "Quoted",
  accepted: "Accepted",
  authorised: "Authorised",
  booked: "Booked",
  done: "Done",
  failed: "Failed",
};

export interface SafedWorklistTestView extends Omit<SafedLifecycleTestView, "state" | "stateLabel"> {
  state: SafedWorklistStateView;
  stateLabel: string;
  quoteRef: string | null;
}

export interface SafedWorklistItemView extends Omit<SafedLiftLifecycleView, "tests" | "counts"> {
  tests: SafedWorklistTestView[];
}

export interface SafedDeliveryPreviewView {
  preview: true;
  to: string | null;
  subject: string;
  attachments: string[];
  notes: string[];
}
export interface SafedDeliveryDoneView {
  preview: false;
  draftId: string;
  to: string;
  subject: string;
}

export interface SafedInvoicePlanView {
  customerName: string;
  reference: string;
  lines: { description: string; amountExVat: number }[];
  totalExVat: number;
}

export interface SafedSignOffView {
  engineerId: string;
  testGroup: "traction" | "hydraulic";
  signedOffAt: string;
  note?: string | null;
  signedOffByName?: string | null;
}
